/**
 * SetupPage - Public Bootstrap Page for Fresh Deployments
 * 
 * This page allows creating the first admin user on a fresh database.
 * NO ENV VARS REQUIRED - self-sufficient via /api/system/setup-status endpoint.
 * 
 * BEHAVIOR:
 * - If admins exist: Shows list of existing admins, blocks creation
 * - If no admins exist: Shows create form
 * - After creation: Page locks permanently (shows admin list)
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SetupPage() {
  const navigate = useNavigate();
  const [setupStatus, setSetupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [email, setEmail] = useState("hello@freeservicepro.com");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("Platform Admin");

  // Fetch setup status on load
  useEffect(() => {
    const fetchSetupStatus = async () => {
      try {
        const response = await fetch(`${API}/system/setup-status`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        setSetupStatus(data);
      } catch (err) {
        console.error("Failed to fetch setup status:", err);
        setError("Failed to connect to server. Please check your deployment.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchSetupStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/admin/system/seed-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          confirm: "SEED_ADMIN_USER",
          email,
          password,
          name,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === "success") {
        setSuccess(true);
        setCredentials(data.credentials);
        toast.success("Admin account created successfully!");
        
        // Refresh setup status to show the new admin
        const refreshResponse = await fetch(`${API}/system/setup-status`);
        if (refreshResponse.ok) {
          setSetupStatus(await refreshResponse.json());
        }
      } else {
        // Handle specific errors
        const errorMsg = data.detail?.error || data.error || data.detail || "Failed to create admin";
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error("Setup failed:", err);
      toast.error("Setup failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-white text-lg">Checking setup status...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-red-500/20 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full text-center border border-red-500/40">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-white mb-2">Connection Error</h1>
          <p className="text-red-200 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Admins already exist - show locked state with admin list
  if (setupStatus && !setupStatus.can_create) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-2xl w-full border border-white/20">
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">🔒</div>
            <h1 className="text-2xl font-bold text-white">Setup is Locked</h1>
            <p className="text-slate-400 text-sm mt-2">
              Platform admin(s) already exist. Admin creation is disabled.
            </p>
          </div>
          
          {/* Existing Admins List */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
            <h2 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wider">
              Existing Platform Admins ({setupStatus.admin_count})
            </h2>
            <div className="space-y-3">
              {setupStatus.admins.map((admin, idx) => (
                <div 
                  key={admin.user_id || idx}
                  className="bg-slate-700/50 rounded-lg p-3 border border-slate-600/50"
                >
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    <div>
                      <span className="text-slate-500 text-xs block">Email</span>
                      <span className="text-white font-mono">{admin.email}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs block">Role</span>
                      <span className="text-orange-400">{admin.role}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs block">Verified</span>
                      <span className={admin.email_verified ? "text-green-400" : "text-red-400"}>
                        {admin.email_verified ? "Yes" : "No"}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs block">Tenant ID</span>
                      <span className="text-slate-300 font-mono text-xs">
                        {admin.tenant_id || "null (platform)"}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs block">User ID</span>
                      <span className="text-slate-400 font-mono text-xs">{admin.user_id}</span>
                    </div>
                    {admin.created_at && (
                      <div>
                        <span className="text-slate-500 text-xs block">Created</span>
                        <span className="text-slate-400 text-xs">
                          {new Date(admin.created_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <button
            onClick={() => navigate("/")}
            className="w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors"
            data-testid="goto-login-btn"
          >
            Go to Login →
          </button>
        </div>
      </div>
    );
  }

  // Success state - show credentials and locked admin list
  if (success && credentials) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full border border-white/20">
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">✅</div>
            <h1 className="text-2xl font-bold text-white">Setup Complete!</h1>
            <p className="text-slate-400 text-sm mt-2">
              Your admin account has been created.
            </p>
          </div>
          
          <div className="bg-green-500/20 border border-green-500/40 rounded-lg p-4 mb-6">
            <p className="text-green-200 text-sm mb-3 font-medium">Your login credentials:</p>
            <div className="space-y-2">
              <div>
                <span className="text-slate-400 text-xs">Email:</span>
                <p className="text-white font-mono">{credentials.email}</p>
              </div>
              <div>
                <span className="text-slate-400 text-xs">Password:</span>
                <p className="text-white font-mono">{credentials.password}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-amber-500/20 border border-amber-500/40 rounded-lg p-3 mb-6">
            <p className="text-amber-200 text-xs">
              ⚠️ <strong>Important:</strong> Change your password immediately after first login!
            </p>
          </div>
          
          <button
            onClick={() => navigate("/")}
            className="w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors"
            data-testid="goto-login-btn"
          >
            Go to Login →
          </button>
        </div>
      </div>
    );
  }

  // Setup form - no admins exist, allow creation
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full border border-white/20">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_tradespro-1/artifacts/b15t91h1_Free-Service-Pro-Logo.png"
            alt="Free Service Pro"
            className="h-12 mx-auto mb-4 brightness-0 invert opacity-90"
          />
          <h1 className="text-2xl font-bold text-white">Initial Setup</h1>
          <p className="text-slate-400 text-sm mt-1">Create your first admin account</p>
        </div>
        
        {/* Info banner */}
        <div className="bg-blue-500/20 border border-blue-500/40 rounded-lg p-3 mb-6">
          <p className="text-blue-200 text-xs">
            No platform admins exist. Create the first admin account to get started.
            This account will have <code className="bg-black/30 px-1 rounded">role: administrator</code> with email verified.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
              placeholder="admin@yourcompany.com"
              required
              data-testid="setup-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Password *
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
              placeholder="Min 8 characters"
              required
              minLength={8}
              data-testid="setup-password-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Confirm Password *
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
              placeholder="Confirm your password"
              required
              data-testid="setup-confirm-password-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Your Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
              placeholder="Your name"
              data-testid="setup-name-input"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors mt-6"
            data-testid="setup-submit-btn"
          >
            {submitting ? "Creating Account..." : "Create Admin Account"}
          </button>
        </form>

        {/* Footer note */}
        <p className="text-slate-500 text-xs text-center mt-6">
          This page will lock permanently after the first admin is created.
        </p>
      </div>
    </div>
  );
}
